import 'babel-polyfill'
import 'intersection-observer'
import './scss/style.scss'
import '@webcomponents/custom-elements'
// clr-icon
import '@clr/icons'
import '@clr/icons/shapes/all-shapes'
// font awesome
import 'vue-awesome/icons/paw'
import 'vue-awesome/icons/product-hunt'
import 'vue-awesome/icons/spinner'

import * as fastclick from 'fastclick'
import Vue from 'vue'
import VueLazyLoad from 'vue-lazyload'
import VueAnalytics from 'vue-analytics'
import VueTouch from 'vue-touch'
import { DynamicScroller, DynamicScrollerItem, RecycleScroller } from 'vue-virtual-scroller'
// buefy
import Buefy from 'buefy'
import Icon from 'vue-awesome/components/Icon.vue'
import feature from 'vue-feature-toggle'
import store from '@/store'
import App from '@/App.vue'
import router from '@/router'
import Actionsheet from '@/components/Actionsheet'
import AmataBanner from '@/components/AmataBanner.vue'
import AmataModal from '@/components/AmataModal'
import AmataDialog from '@/components/AmataDialog'
import AppNavbar from '@/components/AppNavbar.vue'
import AppNavbarButton from '@/components/AppNavbarButton.vue'
import ImgGuard from '@/components/ImgGuard.vue'
import VideoGuard from '@/components/VideoGuard.vue'
import CordovaMixin from '@/mixins/CordovaMixin'
import GlobalMixin from './mixins/GlobalMixin'
import * as Raven from 'raven-js'
import RavenVue from 'raven-js/plugins/vue'
import ravenSendData from '@/raven-send-data'
import * as Url from 'url'
import http from '@/api/http'
import config from '@/config'
import { $ } from '@/utils/helpers'
import { hasCordova, platformIsAndroid, platformIsIOS } from '@/cordova'

// feature flags
feature.visibility('timeline', config.FLAG_TIMELINE)

fastclick.attach(document.body)

Raven.config(config.SENTRY_DSN)
  .addPlugin(RavenVue, Vue)
  .install()

Vue.config.productionTip = false
Vue.config.performance = process.env.NODE_ENV === 'development'
Vue.config.errorHandler = handleError

Vue.use(Buefy)
Vue.use(VueTouch, { name: 'v-touch' })
const gif = require('@/assets/dummy-transparent.gif')
Vue.use(VueLazyLoad, {
  preLoad: 3.0,
  loading: gif,
  error: gif
})

Vue.mixin(CordovaMixin)
Vue.mixin(GlobalMixin)

Vue.component('dynamic-scroller', DynamicScroller)
Vue.component('dynamic-scroller-item', DynamicScrollerItem)
Vue.component('recycle-scroller', RecycleScroller)
Vue.component('amata-banner', AmataBanner)
Vue.component('img-guard', ImgGuard)
Vue.component('video-guard', VideoGuard)
Vue.component('icon', Icon)
Vue.component('app-navbar', AppNavbar)
Vue.component('app-navbar-button', AppNavbarButton)
Vue.component('feature', feature)

Vue.prototype.$http = http
Vue.prototype.$actionsheet = Actionsheet
Vue.prototype.$amatamodal = AmataModal
Vue.prototype.$amatadialog = AmataDialog

if (hasCordova) {
  document.addEventListener('deviceready', handleDeviceReady, false)
} else {
  // Google Analytics - Web
  Vue.use(VueAnalytics, {
    id: config.GOOGLE_ANALYTICS_TRAKING_ID,
    router
  })

  main({ isApp: '0' })
}

function main(options: { isApp: '0' | '1' }) {
  // token があるなら header に付与
  const token = store.getters['Auth/token']
  if (token) {
    http.defaults.headers.common['TOKEN'] = token
  }
  http.defaults.headers.common['app'] = options.isApp

  // 起動毎にベースページのキャッシュをクリア
  store.commit('TopUser/resetState')
  store.commit('MessageList/resetState')
  store.commit('Timeline/resetState')
  store.commit('TimelineUser/resetState')
  // NOTE: 毎回足あとの件数などが0になって鬱陶しいので
  // store.commit('Footprint/resetState')

  /* tslint:disable no-unused-expression */
  const vue = new Vue({
    store,
    router,
    render: h => h(App)
  }).$mount('#app')

  if (options.isApp === '1') {
    navigator.splashscreen.hide()
  }

  if (platformIsAndroid) {
    // 不在着信の local push タップでメッセージ画面に遷移
    cordova.plugins.notification.local.on('click', notification => {
      if (notification.id) {
        vue.$root.$router.push('/message/' + notification.id)
      }
    })
  } else if (platformIsIOS) {
    // CallKit
    if (cordova.plugins.CordovaCall) {
      cordova.plugins.CordovaCall.setIncludeInRecents(true)
      cordova.plugins.CordovaCall.setIcon('call_icon')
      cordova.plugins.CordovaCall.on('answer', () => {
        vue.$root.$emit('cordovaCallAnswer')
      })
      cordova.plugins.CordovaCall.on('reject', () => {
        vue.$root.$emit('cordovaCallReject')
      })
      cordova.plugins.CordovaCall.on('hangup', () => {
        vue.$root.$emit('cordovaCallHangup')
      })
    }
  }
}

async function handleDeviceReady() {
  if (platformIsIOS) {
    // phonegap-plugin-push の 2.1.3 より上のバージョンだと document.hasFocus() による
    // coldstart の変わりのタップ判定が動作しないので window.focus() で document.hasFocus() を有効化
    window.focus()
  }

  // 起動後3秒間は準備中
  sessionStorage.setItem('isInitializing', 'true')
  setTimeout(() => {
    sessionStorage.removeItem('isInitializing')
  }, config.INIT_MSEC)

  // adjust
  const adjustEnvironment = window.BuildInfo.debug
    ? window.AdjustConfig.EnvironmentSandbox
    : window.AdjustConfig.EnvironmentProduction
  const adjustConfig = new window.AdjustConfig(config.ADJUST_API_TOKEN, adjustEnvironment)
  adjustConfig.setEventTrackingSucceededCallbackListener(eventSuccess => {
    console.log('Event tracking succeeded!')
    console.log(eventSuccess.message)
    console.log(eventSuccess.timestamp)
    console.log(eventSuccess.eventToken)
    console.log(eventSuccess.adid)
    console.log(eventSuccess.jsonResponse)
  })
  adjustConfig.setEventTrackingFailedCallbackListener(eventFailure => {
    console.log('Event tracking failed!')
    console.log(eventFailure.message)
    console.log(eventFailure.timestamp)
    console.log(eventFailure.eventToken)
    console.log(eventFailure.adid)
    console.log(eventFailure.jsonResponse)
  })
  adjustConfig.setSessionTrackingSucceededCallbackListener(sessionSuccess => {
    console.log('Session tracking succeeded!')
    console.log(sessionSuccess.message)
    console.log(sessionSuccess.timestamp)
    console.log(sessionSuccess.adid)
    console.log(sessionSuccess.jsonResponse)
  })
  adjustConfig.setSessionTrackingFailedCallbackListener(sessionFailure => {
    console.log('Session tracking failed!')
    console.log(sessionFailure.message)
    console.log(sessionFailure.timestamp)
    console.log(sessionFailure.adid)
    console.log(sessionFailure.willRetry)
    console.log(sessionFailure.jsonResponse)
  })
  window.Adjust.create(adjustConfig)

  store.commit('AppInfo/setAppName', config.APP_NAME)

  // アプリなら Cordova からバージョンを取得
  const appVersion = window.BuildInfo.version
  if (appVersion && appVersion !== store.getters['AppInfo/appVersion']) {
    // NOTE: ここで AppInfo/setAppVersion を実行しても初回起動時は反映されず、2度めの起動時から反映される
    store.commit('AppInfo/setAppVersion', appVersion)
    sessionStorage.setItem('appVersion', appVersion)
  }

  // Google Analytics - App
  window.ga.startTrackerWithId(config.GOOGLE_ANALYTICS_TRAKING_ID)
  window.ga.setAppVersion(appVersion)

  if (platformIsAndroid) {
    // ここにくるまでで 15s 以上かかっているなら WebView の更新を勧める
    const beforeInitialTime = Number(sessionStorage.getItem('beforeInitialTime'))
    const diff = Math.floor((Date.now() - beforeInitialTime) / 1000)
    if (diff > 15) {
      alert(config.MSG_WEBVIEW_VERSION)
    }

    // カメラとマイクを先に許可しておかないと電話発信時にアプリが落ちる
    const permissions = cordova.plugins.permissions
    const checkPermissions = [
      permissions.CAMERA,
      permissions.RECORD_AUDIO,
      permissions.READ_EXTERNAL_STORAGE,
      permissions.WRITE_EXTERNAL_STORAGE
    ]
    await CordovaMixin.methods.$_requestAndroidPermissions(checkPermissions)

    // データ保存用ディレクトリの準備
    const existsPath = await CordovaMixin.methods.$_existsPath(
      cordova.file.externalRootDirectory + config.APP_DATA_DIR
    )
    if (!existsPath) {
      await CordovaMixin.methods.$_createDir(
        cordova.file.externalRootDirectory,
        config.APP_DATA_DIR
      )
    }
  } else if (platformIsIOS) {
    // iOS は一律でアクセサリーバーをキーボードに表示
    Keyboard.hideFormAccessoryBar(false)
    // // スワイプバック許可
    // window.WkWebView.allowsBackForwardNavigationGestures(true)
  }

  main({ isApp: '1' })
}

function handleError(err: Error | ErrorEvent) {
  Raven.setTagsContext(ravenSendData.tagsContext)
  Raven.setUserContext(ravenSendData.userContext)
  Raven.captureException(err)
}

// global 登録しないと webpack でモジュール化されてしまう
// @ts-ignore
window.handleOpenURL = (url: string) => {
  console.log('[handleOpenURL]', url)
  if (typeof window.SafariViewController !== 'undefined') {
    window.SafariViewController.hide()
  }
  setTimeout(() => {
    const urlObj = Url.parse(url, true)
    let reloadURL = ''
    if (urlObj.host) {
      reloadURL += '#/' + urlObj.host
    }
    if (urlObj.path) {
      reloadURL += urlObj.path
    }
    if (reloadURL !== '/' && reloadURL.length > 1) {
      location.href = reloadURL
      // reload
      let _app: any = $('#app')
      if (_app && _app.hasOwnProperty('__vue__')) {
        _app['__vue__'].$root.$emit('routerViewKeyChange', new Date().getTime())
        _app = null
      }
    }
  })
}

window.addEventListener('error', handleError, false)
